import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Wand2, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canFinancial } from "@/lib/permissions";
import { fmtMoney, todayISO } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { DataTable, Btn, TextInput, Select } from "@/components/DataTable";
import { PriorityBadge } from "@/components/StatusBadge";

export default function Unscheduled() {
  const { user } = useAuth();
  const fin = canFinancial(user);
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/visits?unscheduled=true");
      setRows(data.filter((v) => v.status === "Unscheduled" || !v.consultant_id));
    } finally { setLoading(false); }
  };
  useEffect(() => { api.get("/consultants").then((r) => setConsultants(r.data)); load(); }, []);

  const assign = async (v) => {
    const a = assigning[v.id] || {};
    if (!a.date || !a.consultant_id) return toast.error("Choose date and consultant");
    try {
      await api.put(`/visits/${v.id}`, { scheduled_date: a.date, consultant_id: a.consultant_id, status: "Scheduled" });
      toast.success("Visit scheduled");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.conflicts) toast.error("Conflict: " + detail.conflicts.map((c) => c.message).join("; "));
      else toast.error(apiError(e));
    }
  };

  const columns = [
    { key: "visit_ref", label: "Ref", className: "text-xs text-slate-400" },
    { key: "job_title", label: "Job", className: "font-medium text-slate-700" },
    { key: "client_name", label: "Client" },
    { key: "site_name", label: "Site", render: (r) => <span className="text-xs">{r.site_name}</span> },
    { key: "priority", label: "Priority", render: (r) => <PriorityBadge priority={r.priority} /> },
    ...(fin ? [{ key: "charge", label: "Charge", align: "right", render: (r) => fmtMoney(r.charge) }] : []),
    { key: "assign", label: "Quick schedule", render: (r) => (
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <TextInput type="date" min={todayISO()} value={assigning[r.id]?.date || ""} data-testid={`assign-date-${r.id}`}
          onChange={(e) => setAssigning({ ...assigning, [r.id]: { ...assigning[r.id], date: e.target.value } })} className="w-36 py-1" />
        <Select value={assigning[r.id]?.consultant_id || ""} data-testid={`assign-consultant-${r.id}`}
          onChange={(e) => setAssigning({ ...assigning, [r.id]: { ...assigning[r.id], consultant_id: e.target.value } })} className="w-40 py-1">
          <option value="">Consultant…</option>
          {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Btn onClick={() => assign(r)} className="py-1 px-2" data-testid={`assign-btn-${r.id}`}>Go</Btn>
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Unscheduled Work" subtitle="Recurring and one-off jobs awaiting a slot" icon={CalendarClock}
        actions={<Btn variant="forest" onClick={() => nav("/auto-scheduler")} data-testid="goto-autoscheduler"><Wand2 size={16} /> Auto-schedule</Btn>} />
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <Loading /> : <DataTable columns={columns} rows={rows}
          empty={<EmptyState icon={CheckCircle2} title="All caught up" hint="No unscheduled work — every recurring job has a slot." />} />}
      </div>
    </div>
  );
}
